import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export function NewUserRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isClaimRoute = (path: string) => /^\/claim\/[^/]+$/.test(path);

    const isOnboardingCompleteInDb = async (userId: string) => {
      const { data } = await supabase
        .from('users')
        .select('onboarding_completed')
        .eq('id', userId)
        .single();
      return data?.onboarding_completed === true;
    };

    const checkNewUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user?.user_metadata?.isNewUser) {
        // Prefer DB onboarding state as the durable source of truth.
        // If DB says onboarding is complete, don't hijack navigation.
        try {
          const done = await isOnboardingCompleteInDb(session.user.id);
          if (done) {
            await supabase.auth.updateUser({
              data: { isNewUser: false, needsOnboarding: false },
            });
            return;
          }
        } catch {
          // Fallback to metadata logic below if DB check fails.
        }

        if (session?.user?.user_metadata?.needsOnboarding) {
          // Don't redirect away from claim page - let user complete venue claim first
          if (isClaimRoute(location.pathname)) return;
          // Avoid hijacking deep links. Only auto-start onboarding from home.
          if (location.pathname === '/') {
            navigate('/onboarding', { replace: true });
          }
        } else {
          // Only redirect if not already on profile page and onboarding is complete
          if (!location.pathname.includes('/profile/')) {
            // Get the user's username for the profile URL
            const { data: userProfile } = await supabase
              .from('users')
              .select('username')
              .eq('id', session.user.id)
              .single();

            if (userProfile?.username) {
              // Redirect to their profile page
              navigate(`/profile/${userProfile.username}`, { replace: true });
            }

            // Clear the isNewUser flag
            await supabase.auth.updateUser({
              data: { isNewUser: false }
            });
          }
        }
      }
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user?.user_metadata?.isNewUser) {
          // Prefer DB onboarding state as the durable source of truth.
          try {
            const done = await isOnboardingCompleteInDb(session.user.id);
            if (done) {
              await supabase.auth.updateUser({
                data: { isNewUser: false, needsOnboarding: false },
              });
              return;
            }
          } catch {
            // Fallback to metadata logic below if DB check fails.
          }

          if (session?.user?.user_metadata?.needsOnboarding) {
            // Don't redirect away from claim page - let user complete venue claim first
            if (isClaimRoute(location.pathname)) return;
            // Avoid hijacking deep links. Only auto-start onboarding from home.
            if (location.pathname === '/') {
              navigate('/onboarding', { replace: true });
            }
          } else {
            // Only redirect if not already on profile page and onboarding is complete
            if (!location.pathname.includes('/profile/')) {
              // Get the user's username for the profile URL
              const { data: userProfile } = await supabase
                .from('users')
                .select('username')
                .eq('id', session.user.id)
                .single();

              if (userProfile?.username) {
                // Redirect to their profile page
                navigate(`/profile/${userProfile.username}`, { replace: true });
              }

              // Clear the isNewUser flag
              await supabase.auth.updateUser({
                data: { isNewUser: false }
              });
            }
          }
        }
      }
    );

    checkNewUser();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location]);

  return null;
}










